import Panel from '@/components/ui/Panel';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/redux/store';
import { closeCartPanel, setLastAddedItem } from '@/redux/slices/cartSlice';
import Button from '../Buttons/Button';
import classnames from 'classnames';
import Link from '../Link';
import { useCart } from './hooks/useCart';
import { ScreenSizeQueries } from '@/constants/breakpoints';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { shouldShow, markAsDismissed } from '@/utils/frequencyBasedPresentation';
import { useEffect, useMemo, useState, useCallback, useContext } from 'react';
import MyCart from './MyCart/index';
import { IContent } from '@/@types/cms';
import type { CartPanelIMUpsellBlock as CartPanelIMUpsellBlockType } from '@/@types/content';
import {
  CartPanelIMUpsellBlockBody,
  CartPanelIMUpsellBlockFooter
} from '@/components/blocks/CartPanelIMUpsellBlock';
import { trackSelectCartEvent } from '@/analytics/ecommerce';
import { AnalyticsContext } from '@/analytics/constants';
import { trackSelectCheckoutEvent } from '@/analytics/ecommerce';
import { MemberSavingsText } from './MemberSavingsText';
import { creditPlanContext } from '@/contexts/creditPlan/context';

const CartPanel = () => {
  useEffect(() => {
    console.info('CartPanel is enabled');
  }, []);

  const dispatch = useDispatch();
  const { closePanel } = useContext(creditPlanContext);
  const cartState = useSelector((state: RootState) => state.cart);
  const authState = useSelector((state: RootState) => state.auth);
  const {
    subTotal,
    checkout,
    cartPageData,
    cartData,
    isUpdating: isCartUpdating,
    cartPath,
    isCartEmpty,
    isError
  } = useCart();

  const [isCartActionsDisabled, setIsCartActionsDisabled] = useState(false);
  const [showIMUpsellBlock, setShowIMUpsellBlock] = useState(false);

  const sm = useMediaQuery(ScreenSizeQueries.sm);
  const panelContentStyles = classnames({ 'w-[628px]': sm }, { 'w-full': !sm });
  const isCartBusy = isCartActionsDisabled || isCartUpdating || cartState.isAddingToCart;

  const cartPanelIMUpsellBlock = useMemo(() => {
    return cartPageData?.cartPanelContentArea?.find((x: IContent) =>
      x.contentType?.includes('CartPanelIMUpsellBlock')
    ) as CartPanelIMUpsellBlockType;
  }, [cartPageData]);

  const frequencyConfig = useMemo(
    () => ({
      key: 'cartPanelIMUpsell',
      id: String(cartPanelIMUpsellBlock?.contentLink?.id),
      frequency: cartPanelIMUpsellBlock?.frequencyOfPresentation
    }),
    [cartPanelIMUpsellBlock?.contentLink?.id, cartPanelIMUpsellBlock?.frequencyOfPresentation]
  );

  const lastAddedOrderLine = useMemo(
    () => cartData?.orderLines?.find(line => line.code === cartState.lastAddedItem?.id),
    [cartData?.orderLines, cartState.lastAddedItem?.id]
  );

  const handleUpsellBlockVisibility = useCallback(
    (show: boolean) => {
      setShowIMUpsellBlock(show);
      if (!show) {
        markAsDismissed(frequencyConfig);
      }
    },
    [frequencyConfig]
  );

  useEffect(() => {
    if (!cartState.lastAddedItem) {
      return;
    }

    const lastAddedItemCategories = cartState.lastAddedItem.categories ?? [];
    const lastItemIsProgram = lastAddedItemCategories.includes('Program');
    const lastItemIsOnDemandProgram =
      lastItemIsProgram && lastAddedItemCategories.includes('On-Demand');
    const isOnDemandPoecProgram =
      lastItemIsOnDemandProgram && (lastAddedOrderLine?.isPoec ?? false);
    const shouldWaitForOrderLine = lastItemIsOnDemandProgram && !lastAddedOrderLine;

    // Wait for the refreshed order line so we can read the POEC flag for on-demand programs.
    if (shouldWaitForOrderLine) {
      return;
    }

    const shouldShowBasedOnFrequency = shouldShow(frequencyConfig);

    const IMVariant = cartPanelIMUpsellBlock?.catalogVariant?.at(0) as { code: string };
    const isRetailUser = !authState.isIm && !authState.isPm;
    const IMVariantNotInCart = !cartData?.orderLines?.some(line => line.code === IMVariant?.code);
    const lastItemIsNotIMVariant = cartState.lastAddedItem?.id !== IMVariant?.code;

    const shouldShowUpsell =
      isRetailUser &&
      IMVariant &&
      IMVariantNotInCart &&
      lastItemIsNotIMVariant &&
      lastItemIsProgram &&
      !isOnDemandPoecProgram &&
      shouldShowBasedOnFrequency;

    setShowIMUpsellBlock(shouldShowUpsell);
    dispatch(setLastAddedItem(undefined));
  }, [
    cartPanelIMUpsellBlock,
    cartData,
    authState,
    frequencyConfig,
    cartState.lastAddedItem,
    lastAddedOrderLine,
    setShowIMUpsellBlock,
    dispatch
  ]);

  const handleCartBusyStateChange = (isBusy: boolean) => {
    setIsCartActionsDisabled(isBusy);
  };

  const handleOpenChange = () => {
    setShowIMUpsellBlock(false);
    dispatch(closeCartPanel());
  };

  const handleViewCartClick = () => {
    trackSelectCartEvent(AnalyticsContext.CartPanel);
    closePanel();
    dispatch(closeCartPanel());
  };

  const handleCheckoutClick = useCallback(() => {
    trackSelectCheckoutEvent(AnalyticsContext.CartPanel);
    closePanel();
    checkout.buttonProps.onClick?.();
  }, [checkout.buttonProps, closePanel]);

  return (
    <Panel
      isOpen={cartState.isOpen}
      onOpenChange={showIMUpsellBlock ? () => handleUpsellBlockVisibility(false) : handleOpenChange}
      size="medium"
      dataComponent="CartPanel"
    >
      <Panel.Content
        side="right"
        hasHorizontalPadding={false}
        className={panelContentStyles}
        preventOutsideClose={showIMUpsellBlock}
      >
        <Panel.Body>
          {showIMUpsellBlock && cartPanelIMUpsellBlock ? (
            <CartPanelIMUpsellBlockBody
              {...cartPanelIMUpsellBlock}
              close={() => handleUpsellBlockVisibility(false)}
            />
          ) : (
            <MyCart context="panel" onBusyStateChange={handleCartBusyStateChange} />
          )}
        </Panel.Body>

        {isError ? (
          <></>
        ) : isCartEmpty && !isCartBusy ? (
          <Panel.Footer>
            <div className="py-4">
              <Button
                label={cartPageData?.emptyCartBrowseButtonLabel}
                href={cartPageData?.emptyCartBrowseLink?.url}
                size="large"
                className="w-full"
                onClick={handleOpenChange}
              />
            </div>
          </Panel.Footer>
        ) : (
          <Panel.Footer
            className={classnames('border-t border-solid border-silver !px-16 !py-8', {
              'opacity-40': isCartBusy && !showIMUpsellBlock
            })}
          >
            {showIMUpsellBlock && cartPanelIMUpsellBlock ? (
              <CartPanelIMUpsellBlockFooter
                {...cartPanelIMUpsellBlock}
                close={() => handleUpsellBlockVisibility(false)}
              />
            ) : (
              <>
                <div className="flex items-center justify-between pb-6">
                  <span className="text-1">Subtotal</span>
                  <span className="heading-5">{subTotal}</span>
                </div>
                {cartData?.membershipDiscountValue && cartData?.membershipDiscountValue > 0 ? (
                  <div className="text-1-fixed flex justify-center">
                    <MemberSavingsText cart={cartData} />
                  </div>
                ) : null}
                <div className="py-4">
                  <Button
                    {...checkout.buttonProps}
                    disabled={isCartBusy}
                    size="large"
                    className="w-full"
                    onClick={handleCheckoutClick}
                  />
                </div>
                <div className="flex justify-center">
                  <Link
                    href={cartPath}
                    text="View cart"
                    variant="text-link-2"
                    className="flex self-end"
                    disabled={isCartBusy}
                    onClick={handleViewCartClick}
                  />
                </div>
              </>
            )}
          </Panel.Footer>
        )}
      </Panel.Content>
    </Panel>
  );
};

export default CartPanel;
